import React, {
  forwardRef,
  type ReactNode,
  type Ref,
  useId,
  useRef,
} from 'react';
import {
  type A11yStatusMessageOptions,
  useSelect,
  type UseSelectProps,
} from 'downshift';
import styles from './select.module.scss';
import { assets } from '../assets';

export interface SelectProps<T> {
  items: T[];
  value?: T;
  defaultValue?: T;
  onChange?: (value: T | null) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  itemToString?: (item: T | null) => string;
  isItemDisabled?: (item: T) => boolean;
  label?: string;
  showLabel: boolean;
  name?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;
  getA11yStatusMessage?: (opts: A11yStatusMessageOptions<T>) => string;
  getA11ySelectionMessage?: (opts: A11yStatusMessageOptions<T>) => string;
  'data-testid'?: string;
  renderItem?: (item: T, selected: boolean, highlighted: boolean) => ReactNode;
}

function SelectInner<T>(props: SelectProps<T>, ref: Ref<HTMLInputElement>) {
  const {
    items,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    itemToString = (item) => (item ? String(item) : ''),
    isItemDisabled = () => false,
    label,
    showLabel,
    name,
    id,
    placeholder = '',
    disabled = false,
    invalid = false,
    loading = false,
    getA11yStatusMessage,
    getA11ySelectionMessage,
    'data-testid': testId = 'select',
    renderItem,
  } = props;
  const CaretDown = assets.svg.sensible.CaretDown;

  const generatedId = useId();
  const innerRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);

  const selectProps: UseSelectProps<T> = {
    id: id || generatedId,
    items,
    itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      onChange?.(selectedItem ?? null);
      onBlur?.();
    },
    isItemDisabled,
    ...(value !== undefined && { selectedItem: value }),
    ...(defaultValue !== undefined && { defaultSelectedItem: defaultValue }),
    ...(getA11yStatusMessage && { getA11yStatusMessage }),
    ...(getA11ySelectionMessage && { getA11ySelectionMessage }),
  };

  const {
    isOpen,
    selectedItem,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect(selectProps);

  return (
    <div
      className={styles.select + (invalid ? ` ${styles.invalid}` : '')}
      data-loading={loading}
    >
      {label && (
        <label
          {...getLabelProps()}
          className={styles[showLabel ? 'showLabel' : 'hideLabel']}
        >
          {label}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          ref={(el) => {
            if (typeof ref === 'function') ref(el);
            else if (ref)
              (ref as React.MutableRefObject<HTMLInputElement | null>).current =
                el;
            innerRef.current = el;
          }}
          type="text"
          name={name}
          value={itemToString(selectedItem)}
          tabIndex={-1}
          onFocus={() => {
            selectRef.current?.focus();
          }}
          onChange={() => {}}
          aria-labelledby={getLabelProps().id}
          aria-describedby={`${generatedId}-desc`}
          data-testid={`${testId}-input`}
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
          }}
        />
        <div
          className={styles.input}
          {...getToggleButtonProps({
            disabled,
            onBlur,
            onFocus,
            ref: selectRef,
          })}
          data-testid={`${testId}-toggle`}
        >
          {selectedItem ? (
            itemToString(selectedItem)
          ) : (
            <span>{placeholder}</span>
          )}
          <CaretDown className={styles.caretWrapper}></CaretDown>
        </div>
      </div>

      <ul {...getMenuProps()} data-testid={`${testId}-menu`}>
        {isOpen &&
          items.map((item, index) => {
            const isSelected = selectedItem === item;
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                key={index}
                className={highlightedIndex === index ? styles.highlighted : ''}
                {...getItemProps({ item, index })}
              >
                {renderItem
                  ? renderItem(item, isSelected, isHighlighted)
                  : itemToString(item)}
              </li>
            );
          })}
      </ul>
    </div>
  );
}

export const Select = forwardRef(SelectInner) as <T>(
  props: SelectProps<T> & { ref?: Ref<HTMLInputElement> },
) => ReturnType<typeof SelectInner>;
